
import { useParams, useNavigate } from "react-router-dom";
import { Modules } from "@/components/module/Modules";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, Users, Award, Clock, Flame, Star, Zap } from "lucide-react";

/**
 * TopicPage component - Displays modules for a specific topic with Centse brand aesthetic
 * Features vibrant Centse brand gradients, bold typography, and modern design elements
 * Shows a preview of the modules section to encourage scrolling
 */
const TopicPage = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();

  // Fetch topic details for the page header
  const { data: topic, isLoading: isLoadingTopic } = useQuery({
    queryKey: ['topicDetails', topicId],
    queryFn: async () => {
      console.log("🔍 Fetching topic details:", topicId);
      const { data, error } = await supabase
        .from('topics')
        .select('*')
        .eq('id', topicId)
        .maybeSingle();
      
      if (error) {
        console.error("❌ Error fetching topic details:", error);
        throw error;
      }

      console.log("✅ Topic details fetched:", data);
      return data;
    },
    enabled: !!topicId,
  });

  // Fetch modules count for display
  const { data: modulesCount = 0 } = useQuery({
    queryKey: ['modulesCount', topicId],
    queryFn: async () => {
      console.log("📊 Fetching modules count for:", topicId);
      const { count, error } = await supabase
        .from('modules')
        .select('*', { count: 'exact', head: true })
        .eq('topic_id', topicId)
        .eq('status', 'active');
      
      if (error) {
        console.error("❌ Error fetching modules count:", error);
        throw error;
      }

      console.log("📈 Modules count:", count);
      return count || 0;
    },
    enabled: !!topicId,
  });

  // Handle navigation back to the learn dashboard
  const handleBack = () => {
    navigate("/learn/dashboard");
  };

  // Handle module selection - navigate to module page
  const handleSelectModule = (moduleId: string) => {
    console.log("🎯 Navigating to module:", moduleId);
    navigate(`/learn/topic/${topicId}/module/${moduleId}`);
  };

  // Ensure we have a topicId from the route params
  if (!topicId) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-primary to-info flex items-center justify-center p-8 pt-20 md:pt-8">
        <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-8 text-center space-y-6 max-w-md border border-white/30">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto">
            <BookOpen className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-black text-white">Topic Not Found</h2>
          <p className="text-white/80 text-lg">This topic doesn't exist, bestie ✨</p>
          <Button 
            onClick={handleBack} 
            className="bg-white text-primary hover:bg-white/90 font-bold px-8 py-3 rounded-2xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Topics
          </Button>
        </div>
      </div>
    );
  }

  // Loading state with Centse brand skeleton
  if (isLoadingTopic) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-info pt-14 md:pt-0">
        {/* Header skeleton */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-24 h-10 bg-white/20 rounded-2xl animate-pulse" />
              <div className="w-40 h-6 bg-white/20 rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="max-w-6xl mx-auto p-8">
          <div className="space-y-8">
            <div className="w-80 h-12 bg-white/20 rounded-2xl animate-pulse" />
            <div className="w-full h-6 bg-white/20 rounded-xl animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/20 rounded-3xl p-6 space-y-3">
                  <div className="w-full h-6 bg-white/20 rounded-xl animate-pulse" />
                  <div className="w-16 h-4 bg-white/20 rounded-lg animate-pulse mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-info overflow-y-auto pt-14 md:pt-0">
      {/* Floating Nav Header */}
      <div className="sticky top-0 z-50 bg-white/10 backdrop-blur-xl border-b border-white/20">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-white hover:bg-white/20 font-semibold rounded-2xl px-6"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Topics
            </Button>
            {topic && (
              <div className="flex items-center gap-3 text-white/80">
                <Zap className="h-5 w-5" />
                <span className="font-medium">{topic.title}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section - reduced height to show peek of modules below */}
      {topic && (
        <div className="relative overflow-hidden min-h-[calc(100vh-8rem)]">
          {/* Decorative elements with Centse brand colors */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-32 h-32 bg-primary/20 rounded-full blur-xl" />
            <div className="absolute top-40 right-20 w-24 h-24 bg-secondary/20 rounded-full blur-xl" />
            <div className="absolute bottom-20 left-1/2 w-40 h-40 bg-info/20 rounded-full blur-xl" />
          </div>
          
          <div className="relative max-w-6xl mx-auto px-8 py-12">
            <div className="text-center space-y-8">
              {/* Title with gradient text */}
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border border-white/30">
                  <Flame className="h-5 w-5 text-primary" />
                  <span className="text-white font-semibold">Popular Topic</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-white leading-tight">
                  {topic.title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
                  {topic.description}
                </p>
              </div>

              {/* Stats Cards with Centse brand colors */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-4 md:p-6 border border-white/30 text-center hover:bg-white/25 transition-all duration-300 hover:scale-105">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-info to-primary rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-white">{modulesCount}</div>
                  <div className="text-white/80 font-medium text-sm md:text-base">Modules</div>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-4 md:p-6 border border-white/30 text-center hover:bg-white/25 transition-all duration-300 hover:scale-105">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-white">2.5k+</div>
                  <div className="text-white/80 font-medium text-sm md:text-base">Learners</div>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-4 md:p-6 border border-white/30 text-center hover:bg-white/25 transition-all duration-300 hover:scale-105">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-secondary to-info rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Star className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-white">4.8</div>
                  <div className="text-white/80 font-medium text-sm md:text-base">Rating</div>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-4 md:p-6 border border-white/30 text-center hover:bg-white/25 transition-all duration-300 hover:scale-105">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-info to-primary rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Clock className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-white">2-3h</div>
                  <div className="text-white/80 font-medium text-sm md:text-base">Duration</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modules Section with modern container - now visible as a peek */}
      <div className="relative">
        <div className="bg-white rounded-t-[3rem] shadow-xl">
          <div className="max-w-6xl mx-auto pt-8">
            <Modules
              topicId={topicId}
              onBack={handleBack}
              onSelectModule={handleSelectModule}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicPage;
